import { useEffect, useRef } from "react";

type UseSwipeCaptureOptions = {
  /** Whether the swipe-to-capture gesture is active. */
  enabled: boolean;
  /** Invoked when the swipe-to-capture gesture fires. */
  onTrigger: () => void;
  /** Element to attach the swipe-up gesture to (the gesture-capture layer). */
  gestureTargetRef?: React.RefObject<HTMLElement>;
};

// Minimum upward travel (in px) for a one-finger swipe to count as a capture
// gesture, and the maximum horizontal drift allowed for it to still count as
// "up" rather than a pan.
const SWIPE_MIN_DISTANCE = 80;
const SWIPE_MAX_HORIZONTAL = 60;

/**
 * Triggers a manual photo capture on a single-finger upward swipe over the
 * gesture surface — a lower-shake alternative to tapping the capture button.
 */
export const useSwipeCapture = ({
  enabled,
  onTrigger,
  gestureTargetRef,
}: UseSwipeCaptureOptions) => {
  // Keep the latest callback in a ref so the listeners (bound once) always call
  // the freshest handler without re-binding.
  const onTriggerRef = useRef(onTrigger);
  useEffect(() => {
    onTriggerRef.current = onTrigger;
  }, [onTrigger]);

  const lastTriggerRef = useRef(0);

  const fire = () => {
    const now = Date.now();
    // Debounce: ignore triggers within 800ms of the previous one.
    if (now - lastTriggerRef.current < 800) {
      return;
    }
    lastTriggerRef.current = now;
    onTriggerRef.current();
  };

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const el = gestureTargetRef?.current;
    if (!el) {
      return;
    }

    let startX = 0;
    let startY = 0;
    let tracking = false;

    const handleTouchStart = (e: TouchEvent) => {
      // Only track single-finger gestures; two fingers is pinch-zoom.
      if (e.touches.length === 1) {
        tracking = true;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      } else {
        tracking = false;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      // Once a single-finger vertical drag is underway, suppress the browser's
      // native scroll/overscroll so the gesture isn't cancelled mid-swipe.
      if (tracking && e.touches.length === 1) {
        const deltaY = Math.abs(startY - e.touches[0].clientY);
        const deltaX = Math.abs(e.touches[0].clientX - startX);
        if (deltaY > deltaX) {
          e.preventDefault();
        }
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!tracking) {
        return;
      }
      tracking = false;
      const touch = e.changedTouches[0];
      if (!touch) {
        return;
      }
      const deltaY = startY - touch.clientY; // positive = upward
      const deltaX = Math.abs(touch.clientX - startX);
      if (deltaY >= SWIPE_MIN_DISTANCE && deltaX <= SWIPE_MAX_HORIZONTAL) {
        fire();
      }
    };

    // passive:false so preventDefault() on touchmove is honored.
    el.addEventListener("touchstart", handleTouchStart, { passive: false });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd);
    el.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
      el.removeEventListener("touchcancel", handleTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, gestureTargetRef?.current]);
};
